import {use, useEffect, useMemo, useRef} from 'react'
import type {ChatState} from '@microsoft/agent-host-protocol'

import {SessionContext} from '../contexts/SessionContext'
import type {Artifact} from '../utils/artifacts/artifact-core'
import {deriveArtifactsFromChatStates} from '../utils/artifacts/derive-artifacts-from-chat-state'
import {useAhpSessionEvents} from './use-ahp-session-events'
import {useIsAhpSession} from './use-is-ahp-session'
import {useTaskHistoryRequired} from '../contexts/new-task-context'

interface SessionArtifactsResult {
  artifacts: Artifact[]
  sessionId: string | undefined
  /**
   * Whether `artifacts` reflects the session's fully loaded history. It is
   * `false` while a persisted replay the list still depends on has not arrived:
   * a cold load with no live state, a partial live window whose older turns live
   * only in the persisted backfill, or a reconnect refetch that has not yet
   * replaced a replay the live window has moved past. Consumers must not treat
   * an artifact's absence as a removal until this is `true`, or they close tabs
   * for files that are merely unloaded and cannot reopen them once the replay
   * lands.
   */
  historyIsSettled: boolean
  artifactWritesSettled: boolean
}

export interface PersistedArtifactHistory {
  chatState: ChatState | undefined
  refetch: () => void
}

/**
 * The files the active session's agent wrote, derived from the AHP `ChatState`
 * replay and the live session state. Non-AHP sessions return an empty result.
 *
 * Existing sessions with incomplete live history share the persisted
 * session-events query used by the timeline. New conversations use live state
 * until a partial reconnect snapshot requires backfill.
 * Callers that already loaded task history can supply that replay instead.
 */
export function useSessionArtifacts(
  liveChatState?: ChatState,
  persistedHistory?: PersistedArtifactHistory,
): SessionArtifactsResult {
  const sessionContext = use(SessionContext)
  const session = sessionContext?.session ?? null
  const {isAhpSession} = useIsAhpSession(session)
  const sessionId = session?.id
  const environmentId = session?.environment_id
  const contentRefOrigin = useMemo(
    () => (isAhpSession && sessionId && environmentId ? {environmentId, sessionId} : undefined),
    [environmentId, isAhpSession, sessionId],
  )

  const historyRequired = useTaskHistoryRequired(session?.task_id)
  const liveHistoryIsComplete = liveChatState !== undefined && liveChatState.turnsNextCursor === undefined
  const persistedHistoryRequired = isAhpSession && !!sessionId && historyRequired && !liveHistoryIsComplete
  const persistedEnabled = persistedHistoryRequired && !persistedHistory
  const {chatState: fetchedChatState, refetch: refetchSessionEvents} = useAhpSessionEvents({
    session,
    enabled: persistedEnabled,
  })
  const chatState = historyRequired ? (persistedHistory ? persistedHistory.chatState : fetchedChatState) : undefined
  const refetch = persistedHistory?.refetch ?? refetchSessionEvents
  const previousLiveWindowRef = useRef<{sessionId: string; boundary: string} | undefined>(undefined)
  const liveWindowBoundary = liveChatState
    ? JSON.stringify([liveChatState.turnsNextCursor ?? null, liveChatState.turns[0]?.id ?? null])
    : undefined
  const oldestLiveTurnId = liveChatState?.turns[0]?.id
  const persistedCoversLiveWindow =
    chatState !== undefined &&
    (oldestLiveTurnId === undefined || chatState.turns.some(turn => turn.id === oldestLiveTurnId))

  useEffect(() => {
    if (!sessionId || liveWindowBoundary === undefined) return

    const previousLiveWindow = previousLiveWindowRef.current
    previousLiveWindowRef.current = {sessionId, boundary: liveWindowBoundary}
    if (
      previousLiveWindow === undefined ||
      previousLiveWindow.sessionId !== sessionId ||
      previousLiveWindow.boundary === liveWindowBoundary ||
      !persistedHistoryRequired ||
      liveHistoryIsComplete ||
      persistedCoversLiveWindow
    ) {
      return
    }

    void refetch()
  }, [
    liveHistoryIsComplete,
    liveWindowBoundary,
    persistedCoversLiveWindow,
    persistedHistoryRequired,
    refetch,
    sessionId,
  ])

  const artifacts = useMemo(
    () => (isAhpSession ? deriveArtifactsFromChatStates([chatState, liveChatState], {contentRefOrigin}) : []),
    [isAhpSession, liveChatState, chatState, contentRefOrigin],
  )

  // A partial live tail (`turnsNextCursor`) omits older turns that only the
  // persisted backfill supplies, and no live state at all leaves the list
  // entirely on that backfill, so in both cases the list is incomplete until the
  // persisted replay arrives. A complete live snapshot needs no backfill, and a
  // session with no persisted fetch has nothing to wait for.
  //
  // Coverage is a question about the data on hand, not about a fetch being in
  // flight: the replay is current only if it reaches the oldest turn the live
  // window shows. A status flag cannot stand in for this, because consumers read
  // the result in a child effect that React runs before the parent effect that
  // starts the refetch, so the flag would still be false for the render that
  // does the damage.
  const historyIsSettled = liveHistoryIsComplete || !persistedHistoryRequired || persistedCoversLiveWindow
  const state = liveChatState ?? chatState
  const artifactWritesSettled = historyIsSettled && state !== undefined && state.activeTurn === undefined

  return {artifacts, sessionId, historyIsSettled, artifactWritesSettled}
}
